import { List } from "immutable";
import PropTypes from "prop-types";
import React, { Fragment } from "react";

import ToolbarPopoverButton from "../../components/toolbar/ToolbarPopoverButton";
import ToolbarPopoverList from "../../components/toolbar/ToolbarPopoverList";
import ToolbarSpacer from "../../components/toolbar/ToolbarSpacer";
import ToolbarTextButton from "../../components/toolbar/ToolbarTextButton";
import CheckboxStates from "../../constants/checkboxStates";
import {
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { appStateOperations } from "../../modules/appState";
import { mindmapOperations } from "../../modules/mindmaps";
import { immutableRecords, PropTypeShapes } from "../../types";
import { showOnExtraConditionForReadonly } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { trackEvent } from "../../utils/trackAnalytics";

const CREATE_MINDMAP_ID = "create_new_mindmap";
const isReadonly = preprocessorQuery.isReadonly();
const isMobile = preprocessorQuery.isMobile();

const getMindmapOptions = (existingMindmaps, activeMindmapId) => {
  const existingGroup =
    existingMindmaps.size > 0
      ? immutableRecords.PopoverMenuGroupRecord({
        label: "Existing",
        children: existingMindmaps.map(mm =>
          immutableRecords
            .PopoverMenuItemRecord(mm)
            .set("icon", "mindmapView")
            .set("state", mm.id === activeMindmapId ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED)
        )
      })
      : null;
  const createGroup = isReadonly
    ? null
    : immutableRecords.PopoverMenuGroupRecord({
      label: "Create",
      children: List([
        immutableRecords.PopoverMenuItemRecord({
          id: CREATE_MINDMAP_ID,
          icon: "mindmapView",
          label: "New Mindmap"
        })
      ])
    });
  return List([existingGroup, createGroup].filter(g => g));
};

const ActiveViewButtonBar = props => {
  const onViewClick = id => {
    props.onViewTypeClick(appStateOperations.switchViewType(id, null));
    trackEvent("setViewType", "view", id);
  };

  const narrativeSection = props.includeNarrative
    ? showOnExtraConditionForReadonly(
      <Fragment>
        <ToolbarSpacer />
        <ToolbarTextButton
          text={props.iconOnly ? "" : "Narrative"}
          icon="narrativeView"
          iconColor="iconNarrative"
          active={!props.disabled && props.activeViewType === NARRATIVE_VIEW}
          pulse={props.pulsateIconId === interfaceIdentifiers.NARRATIVE}
          onClick={() => onViewClick(NARRATIVE_VIEW)}
          disabled={props.disabled}
          tooltip={props.disabled ? null : "Switch focused view to Narrative View"}
        />
        {!isMobile ? (
          <ToolbarTextButton
            text={props.iconOnly ? "" : "Outline"}
            icon="outlineView"
            iconColor="iconNarrative"
            active={props.activeViewType === OUTLINE_VIEW}
            pulse={props.pulsateIconId === interfaceIdentifiers.OUTLINE}
            onClick={() => onViewClick(OUTLINE_VIEW)}
            disabled={props.disabled}
            tooltip={props.disabled ? null : "Switch focused view to Outline View"}
          />
        ) : null}
      </Fragment>,
      props.hasNarrativeItem
    )
    : null;

  const onMindmapClick = id => {
    if (id === CREATE_MINDMAP_ID) {
      props.onViewTypeClick(mindmapOperations.createMindmap());
      trackEvent("setViewType", "view", "MINDMAP_CREATE");
    } else {
      props.onViewTypeClick(appStateOperations.switchViewType(MINDMAP_VIEW, id));
      trackEvent("setViewType", "view", "MINDMAP_VIEW");
    }
  };

  const mindmapSection = !isMobile
    ? showOnExtraConditionForReadonly(
      <Fragment>
        <ToolbarSpacer />
        <ToolbarPopoverButton placement="bottom-start" disabled={props.disabled}>
          <ToolbarTextButton
            id="toolbarMindmapButton"
            text={props.iconOnly ? "" : "Mindmap"}
            icon="mindmapView"
            iconColor="iconMono"
            active={props.activeViewType === MINDMAP_VIEW}
            pulse={props.pulsateIconId === interfaceIdentifiers.MINDMAP}
            disabled={props.disabled}
            tooltip={props.disabled ? null : "Switch focused view to Mindmap View"}
          />
          <ToolbarPopoverList
            groups={getMindmapOptions(props.existingMindmaps, props.activeViewMindmapId)}
            showIcons={true}
            showBorders={true}
            showSelectedIcon={true}
            onClick={onMindmapClick}
            disabled={props.disabled}
          />
        </ToolbarPopoverButton>
      </Fragment>,
      props.existingMindmaps.size > 0
    )
    : null;

  return (
    <React.Fragment>
      <ToolbarTextButton
        text={props.iconOnly ? "" : "Timeline"}
        icon="timelineView"
        iconColor="iconChronological"
        active={!props.disabled && props.activeViewType === TIMELINE_VIEW}
        pulse={props.pulsateIconId === interfaceIdentifiers.TIMELINE}
        disabled={props.disabled}
        onClick={() => onViewClick(TIMELINE_VIEW)}
        tooltip={props.disabled ? null : "Switch focused view to Timeline View"}
      />
      {!isMobile ? (
        <Fragment>
          <ToolbarTextButton
            text={props.iconOnly ? "" : "Spreadsheet"}
            icon="spreadsheetView"
            iconColor="iconChronological"
            active={props.activeViewType === SPREADSHEET_VIEW}
            pulse={props.pulsateIconId === interfaceIdentifiers.SPREADSHEET}
            onClick={() => onViewClick(SPREADSHEET_VIEW)}
            disabled={props.disabled}
            tooltip={props.disabled ? null : "Switch focused view to Spreadsheet View"}
          />
          <ToolbarTextButton
            text={props.iconOnly ? "" : "Relationship"}
            icon="relationshipView"
            iconColor="iconChronological"
            active={props.activeViewType === RELATIONSHIP_VIEW}
            pulse={props.pulsateIconId === interfaceIdentifiers.RELATIONSHIP}
            onClick={() => onViewClick(RELATIONSHIP_VIEW)}
            disabled={props.disabled}
            tooltip={props.disabled ? null : "Switch focused view to Relationship View"}
          />
          <ToolbarTextButton
            text={props.iconOnly ? "" : "Subway"}
            icon="subwayView"
            iconColor="iconChronological"
            active={props.activeViewType === SUBWAY_VIEW}
            pulse={props.pulsateIconId === interfaceIdentifiers.SUBWAY}
            onClick={() => onViewClick(SUBWAY_VIEW)}
            disabled={props.disabled}
            tooltip={props.disabled ? null : "Switch focused view to Subway View"}
          />
        </Fragment>
      ) : null}

      {narrativeSection}
      {mindmapSection}
    </React.Fragment>
  );
};

ActiveViewButtonBar.propTypes = {
  disabled: PropTypes.bool.isRequired,
  includeNarrative: PropTypes.bool.isRequired,
  activeViewType: PropTypes.string.isRequired,
  activeViewMindmapId: PropTypes.string,
  onViewTypeClick: PropTypes.func.isRequired,
  existingMindmaps: PropTypeShapes.mindmapList.isRequired,
  pulsateIconId: PropTypes.string,
  iconOnly: PropTypes.bool,
  hasNarrativeItem: PropTypes.bool
};

export default ActiveViewButtonBar;
